import { create } from 'zustand';
import type { Medication, CreateMedicationData, UpdateMedicationData } from '../types';
import { medicationService } from '../services/medication.service';

interface MedicationState {
  medications: Medication[];
  selectedMedication: Medication | null;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;

  // Actions
  fetchMedications: () => Promise<void>;
  getMedicationById: (id: string) => Promise<Medication>;
  createMedication: (data: CreateMedicationData) => Promise<Medication>;
  updateMedication: (id: string, data: UpdateMedicationData) => Promise<void>;
  toggleActive: (id: string) => Promise<void>;
  deleteMedication: (id: string) => Promise<void>;
  uploadPhoto: (id: string, file: File) => Promise<string>;
  setSelectedMedication: (medication: Medication | null) => void;
  clearError: () => void;
}

export const useMedicationStore = create<MedicationState>((set, get) => ({
  medications: [],
  selectedMedication: null,
  isLoading: false,
  isUpdating: false,
  error: null,

  fetchMedications: async () => {
    set({ isLoading: true, error: null });
    try {
      const medications = await medicationService.getMedications();
      set({ medications, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch medications';
      set({ error: message, isLoading: false });
    }
  },

  getMedicationById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const medication = await medicationService.getMedicationById(id);
      set({ selectedMedication: medication, isLoading: false });
      return medication;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch medication';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  createMedication: async (data: CreateMedicationData) => {
    set({ isLoading: true, error: null });
    try {
      const newMedication = await medicationService.createMedication(data);
      set((state) => ({
        medications: [...state.medications, newMedication],
        isLoading: false,
      }));
      return newMedication;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create medication';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateMedication: async (id: string, data: UpdateMedicationData) => {
    set({ isUpdating: true, error: null });
    try {
      const updatedMedication = await medicationService.updateMedication(id, data);
      set((state) => ({
        medications: state.medications.map((m) => (m.id === id ? updatedMedication : m)),
        selectedMedication: state.selectedMedication?.id === id ? updatedMedication : state.selectedMedication,
        isUpdating: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update medication';
      set({ error: message, isUpdating: false });
      throw error;
    }
  },

  toggleActive: async (id: string) => {
    const medication = get().medications.find(m => m.id === id);
    if (!medication) return;
    
    set({ isUpdating: true, error: null });
    try {
      const updatedMedication = await medicationService.toggleActive(id, !medication.isActive);
      set((state) => ({
        medications: state.medications.map((m) => (m.id === id ? updatedMedication : m)),
        isUpdating: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to toggle product status';
      set({ error: message, isUpdating: false });
      throw error;
    }
  },

  deleteMedication: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await medicationService.deleteMedication(id);
      set((state) => ({
        medications: state.medications.filter((m) => m.id !== id),
        selectedMedication: state.selectedMedication?.id === id ? null : state.selectedMedication,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete medication';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  uploadPhoto: async (id: string, file: File) => {
    set({ isLoading: true, error: null });
    try {
      const photoUrl = await medicationService.uploadPhoto(id, file);
      set((state) => ({
        medications: state.medications.map((m) =>
          m.id === id ? { ...m, photo: photoUrl } : m
        ),
        selectedMedication:
          state.selectedMedication?.id === id
            ? { ...state.selectedMedication, photo: photoUrl }
            : state.selectedMedication,
        isLoading: false,
      }));
      return photoUrl;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload photo';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setSelectedMedication: (medication: Medication | null) => set({ selectedMedication: medication }),

  clearError: () => set({ error: null }),
}));

